<template>
  <view>
    <popup class="share-earn-popup" title="立即分享给好友" ref="sharePopupRef" :safe-area="false">
      <view class="list">
        <view
          class="item"
          v-for="item in computeConfigList"
          :key="item.key"
          @click="handleItemClick(item)"
        >
          <button
            id="friend"
            v-if="item.key === SharingType.friend"
            class="c-open-type-button"
            open-type="share"
          />
          <c-img :remote-name="item.imgName" :size="88" />
          <view class="item-text">{{ item.text }}</view>
        </view>
      </view>
    </popup>

    <modal class="qrcode-popup" title="立即分享给好友" ref="qrcodePopupRef">
      <view class="qrcode-outer">
        <view class="qrcode-inner">
          <view class="qrcode-img">
            <c-img :size="472" :src="qrcodeUrlRef" showMenuByLongpress />
          </view>





          <view class="qrcode-h5-tips">请长按图片保存到相册</view>

        </view>
      </view>
    </modal>
  </view>
</template>

<script setup lang="ts">
import Modal from '@/components/popup/center.vue';
import { PAGE_PATH } from '@/constants';
import { fetchAddDistributorShareProduct, fetchDistributorSharing } from '@/services';
import { useAppStore, useCustomStore, useUserStore } from '@/store';
import { OutsideComein, SharingType } from '@/types';
import { businessUtils, commonUtils, toast } from '@/utils';
import { computed, onMounted, ref, toRefs } from 'vue';
import { sceneConfig } from '@/local-conf';

// 定义路由映射关系
const ROUTE_SHARE_TYPE_MAP = new Map([
  [PAGE_PATH.home.route, OutsideComein.homePage],
  [PAGE_PATH.classify.route, OutsideComein.commodityGroupPage],
  [PAGE_PATH.activity.route, OutsideComein.customPage],
  [PAGE_PATH.commodity.route, OutsideComein.product],
  [PAGE_PATH.member.route, OutsideComein.memberCenter],
  [PAGE_PATH.memberDetail.route, OutsideComein.memberShipPlan],
  [PAGE_PATH.searchResult.route, OutsideComein.searchResult],
  [PAGE_PATH.integralCommodity.route, OutsideComein.PointsMall],
  [PAGE_PATH.couponDetail.route, OutsideComein.coupon],
  [PAGE_PATH.signIn.route, OutsideComein.checkinPage],
  [PAGE_PATH.forumNoteDetail.route, OutsideComein.forumNote],
  [PAGE_PATH.forumTopicDetail.route, OutsideComein.forumTag],
  [PAGE_PATH.blindBoxActivityV2.route, OutsideComein.wishBoxActivity],
  [PAGE_PATH.raffle.route, OutsideComein.lotteryActivity],
]);

interface Props {
  /** 需分享的数据值 */
  shareValue?: string | number;
}
const props = withDefaults(defineProps<Props>(), {});
const { shareValue } = toRefs(props);

const sharePopupRef = ref<AnyObject>();
const qrcodePopupRef = ref<AnyObject>();
const qrcodeUrlRef = ref('');

const appStore = useAppStore();
const customStore = useCustomStore();

const pages = getCurrentPages();
const currentPage = pages[pages.length - 1];

/** 是否展示h5分享链接 */
const computeIsH5 = computed(() => {
  if (
    [
      PAGE_PATH.home.route,
      PAGE_PATH.activity.route,
      PAGE_PATH.classify.route,
      PAGE_PATH.member.route,
    ].includes(`/${currentPage.route ?? ''}`)
  ) {
    return true;
  }
  return false;
});

/** 配置数据 */
const computeConfigList = computed(() => {
  let config = [







    { key: SharingType.QRCode, text: '小程序码', imgName: 'share_mp' },
    { key: SharingType.link, text: '复制链接', imgName: 'share_link' },
  ];

  if (computeIsH5.value) {
    config.push({ key: SharingType.H5Link, text: 'H5链接', imgName: 'share_h5' });
  }

  return config;
});

// 获取当前页面信息
const getCurrentPageInfo = () => {
  return {
    currentPage,
    matchRoute: `/${currentPage.route}`,
  };
};

// 处理分享
const handleItemClick = async (item: AnyObject) => {
  businessUtils.matomo.customPageClick();

  // （分享好友-卡片,h5卡片 除外，好友分享走页面的onShareAppMessage的流程）
  if (![SharingType.friend, SharingType.H5Link].includes(item.key)) {
    const { currentPage, matchRoute } = getCurrentPageInfo();

    let input: AnyObject = {
      // 服务端使用字段；分享按钮（来源）的类型；必传；
      type: item.key,
      // 服务端使用字段；分享后跳转的路径，不传则跳首页；注：从当前组件场景分享出去的，都是直达，必传；
      path: currentPage.route,
      // 分享的类型，默认none，选传；
      shareType: ROUTE_SHARE_TYPE_MAP.get(matchRoute) || OutsideComein.none,
      // 分享的值，选传；注：慎用缓存里的值，需实时页面相关的值；
      shareValue: shareValue.value,
      // 分销员id，选传；注：不传时，服务端会自行用户的分销id并返回；
      // distributorId: appStore.distributorData?.id || '',
    };

    console.log('<分享分销参数输出>', { input, currentPage });

    const res = await fetchDistributorSharing(input);
    if (!res) return;

    sharePopupRef.value?.close();

    if (item.key === SharingType.link) {
      commonUtils.func.copy(res);
    }

    if (item.key === SharingType.QRCode) {
      qrcodeUrlRef.value = res;
      qrcodePopupRef.value?.open();
    }

    if (input.shareType === OutsideComein.product) {
      // 上报分享出去的商品
      fetchAddDistributorShareProduct(input.shareValue);
    }
  } else if ([SharingType.H5Link].includes(item.key)) {
    // 拼接h5链接
    sharePopupRef.value?.close();

    if ([PAGE_PATH.home.route].includes(`/${currentPage.route ?? ''}`)) {
      commonUtils.func.copy(
        `${sceneConfig.h5Host}/?id=${customStore.homeCustomId}&distributorId=${
          appStore.distributorData?.id || ''
        }`
      );
    } else if ([PAGE_PATH.classify.route].includes(`/${currentPage.route ?? ''}`)) {
      commonUtils.func.copy(
        `${sceneConfig.h5Host}/?id=${customStore.classifyCustomId}&distributorId=${
          appStore.distributorData?.id || ''
        }`
      );
    } else if ([PAGE_PATH.member.route].includes(`/${currentPage.route ?? ''}`)) {
      commonUtils.func.copy(
        `${sceneConfig.h5Host}/?id=${customStore.activityCustomId}&distributorId=${
          appStore.distributorData?.id || ''
        }`
      );
    } else {
      commonUtils.func.copy(
        `${sceneConfig.h5Host}/?id=${customStore.activityCustomId}&distributorId=${
          appStore.distributorData?.id || ''
        }`
      );
    }
  }
};

/** 处理图片操作 */
const disposeImageActions = async () => {
  uni.getImageInfo({
    src: qrcodeUrlRef.value,
    success: (image) => {
      console.log('<获取图片成功>', image);

      uni.saveImageToPhotosAlbum({
        filePath: image.path,
        success: (res) => {
          qrcodePopupRef.value?.close();
          toast.message('保存成功');
          console.log('<保存图片成功>', res);
        },
        fail: (err) => {
          toast.message('保存失败');
          console.log('<保存图片失败>', err);
        },
      });
    },
    fail: (err) => {
      console.log('<获取图片失败>', err);
    },
  });
};

/** 保存图片 */
const handleSaveImage = () => {
  businessUtils.matomo.customPageClick();
































};

onMounted(() => {
  // 兼容没有获取分销员数据的场景
  if (useUserStore().isLogin) {
    appStore.getGetDistributor();
  }
});

defineExpose({
  open: () => sharePopupRef.value?.open(),
  close: () => sharePopupRef.value?.close(),
});
</script>

<style scoped lang="scss">
.share-earn-popup {
  // position: relative;
  position: fixed;
  z-index: 9993;

  .list {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    margin-bottom: 200rpx;
    overflow-x: scroll;

    .item {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      margin-right: 60rpx;
    }

    .item-text {
      white-space: nowrap;
      margin-top: 8rpx;
      font-size: 24rpx;
      color: $N6;
    }
  }
}

.qrcode-popup {
  .qrcode-outer {
    width: 654rpx;
    height: 782rpx;
    padding: 20rpx;
    background: linear-gradient(
      135deg,
      #ffe4f0 0%,
      #ffffff 32%,
      #ffffff 53%,
      #ffffff 67%,
      #ffe4d4 100%
    );
    border-radius: 40rpx;
  }

  .qrcode-inner {
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: 0 60rpx;
    box-shadow: 1px 1px 2px 0px rgba(135, 22, 0, 0.2);
    // border: 4rpx solid;
    // background-image: linear-gradient(
    //   135deg,
    //   rgba(247, 231, 197, 1),
    //   rgba(254, 146, 88, 1),
    //   rgba(254, 251, 217, 1),
    //   rgba(254, 146, 89, 1),
    //   rgba(247, 231, 197, 1)
    // );
    // border-image: linear-gradient(
    //     135deg,
    //     rgba(247, 231, 197, 1),
    //     rgba(254, 146, 88, 1),
    //     rgba(254, 251, 217, 1),
    //     rgba(254, 146, 89, 1),
    //     rgba(247, 231, 197, 1)
    //   )
    //   1 1;
    border-radius: 32rpx;
  }

  .qrcode-img {
    width: 100%;
    height: 494rpx;
    background: $W;
    box-shadow: 0 0 20rpx 0 rgba(135, 22, 0, 0.06);
    border-radius: 20rpx;
    padding: 10rpx 6rpx;
  }

  .qrcode-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 88rpx;
    border-radius: 88rpx;
    background: $M1;
    color: $W;
    margin-top: 40rpx;
  }

  .qrcode-h5-tips {
    margin-top: 40rpx;
    font-size: 24rpx;
    color: $N6;
    text-align: center;
  }
}
</style>
